if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mobileMediaList: this.querySelector('.product__media-list.slider--mobile'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // On desktop, only videos in visible gallery slides should play.
        if (window.matchMedia('(min-width: 750px)').matches) {
          this.syncDesktopVideoPlayback();
          this.desktopVideoObserver = new MutationObserver(debounce(this.syncDesktopVideoPlayback.bind(this), 50));
          this.desktopVideoObserver.observe(this.elements.viewer.slider, {
            attributes: true,
            attributeFilter: ['class', 'style'],
            subtree: true,
          });
        } else {
          this.observeMobileVideoPlayback();
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;

        if (this.mql.matches) this.syncDesktopVideoPlayback();

        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      syncDesktopVideoPlayback() {
        this.querySelectorAll('.product-video-autoplay-desktop').forEach((deferredMedia) => {
          const slide = deferredMedia.closest('.product__media-item');
          const video = deferredMedia.querySelector('video');
          if (!slide || getComputedStyle(slide).display === 'none') {
            if (video) video.pause();
            return;
          }

          if (video) {
            video.play();
          } else {
            deferredMedia.loadContent(false, false);
          }
        });
      }

      observeMobileVideoPlayback() {
        if (!this.elements.mobileMediaList) return;

        this.mobileVideoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const deferredMedia = entry.target.querySelector('.product-video-autoplay-desktop');
              if (!deferredMedia) return;

              const video = deferredMedia.querySelector('video');
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                if (video) {
                  video.play();
                } else {
                  deferredMedia.loadContent(false, false);
                }
              } else if (video) {
                video.pause();
              }
            });
          },
          { root: this.elements.mobileMediaList, threshold: [0, 0.6] }
        );

        this.querySelectorAll('.product-video-autoplay-desktop').forEach((deferredMedia) => {
          const slide = deferredMedia.closest('.product__media-item');
          if (slide) this.mobileVideoObserver.observe(slide);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
